import React, { useState, useEffect } from 'react';
import { getAdminToken } from '../services/apiService';
import { ADMIN_API_ENDPOINTS } from '../config/api';

const ContactInfo = () => {
  const [formData, setFormData] = useState({
    address_en: '',
    address_zh: '',
    phone: '',
    email: '',
  });
  const [originalData, setOriginalData] = useState({
    address_en: '',
    address_zh: '',
    phone: '',
    email: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.CONTACT_INFO, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const contactData = {
          address_en: data.contactInfo.address_en || '',
          address_zh: data.contactInfo.address_zh || '',
          phone: data.contactInfo.phone || '',
          email: data.contactInfo.email || '',
        };
        setFormData(contactData);
        setOriginalData(contactData);
      }
    } catch (error) {
      console.error('获取联系信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.CONTACT_INFO, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: '联系信息已成功更新！' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        // 更新原始数据并退出编辑模式
        setOriginalData(formData);
        setIsEditing(false);
      } else {
        setMessage({ type: 'error', text: data.message || '更新失败' });
      }
    } catch (error) {
      console.error('更新联系信息失败:', error);
      setMessage({ type: 'error', text: '网络错误，请稍后重试' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-400">加载中...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-white mb-6">联系信息管理</h1>

      {/* 消息提示 */}
      {message.text && (
        <div
          className={`mb-6 px-6 py-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-500 text-green-400'
              : 'bg-red-500/20 border border-red-500 text-red-400'
          }`}
        >
          {message.type === 'success' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"></path>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
            </svg>
          )}
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">地址信息</h2>

          <div className="grid grid-cols-1 gap-6">
            {/* 英文地址 */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                地址（英文） *
              </label>
              <input
                type="text"
                name="address_en"
                value={formData.address_en}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="123 Metalworks Ave, Tech City, 90210"
                className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* 中文地址 */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                地址（中文） *
              </label>
              <input
                type="text"
                name="address_zh"
                value={formData.address_zh}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="科技城金属工作大道123号，90210"
                className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">联系方式</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 电话 */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                电话 *
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* 邮箱 */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                支持邮箱 *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="support@metalboxmodels.com"
                className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] disabled:opacity-60 disabled:cursor-not-allowed"
                required
              />
            </div>
          </div>
        </div>

        {/* 按钮区域 */}
        <div className="flex justify-end gap-4">
          {!isEditing ? (
            <button
              type="button"
              onClick={handleEdit}
              className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors"
            >
              修改
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-[#262a22] hover:bg-[#2a2d27] text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    保存中...
                  </>
                ) : (
                  '保存更改'
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContactInfo;
